import '../../GenericTable.css'
import { PageChangeEvent } from '../../Models/pageChangeEvent'
import { JSX, useState, useEffect } from 'react'
import PageIndexButton from './PageIndexButton'


interface PaginatorProps {
  onNewPage: (nextPageEvent: PageChangeEvent) => void;
  firstPage?: number;
  lastPage: number;
  initialIndex: number;
}


function Paginator({ onNewPage, lastPage = 1, initialIndex = 1, firstPage = 1 }: PaginatorProps): JSX.Element {
  const [currentPage, setCurrentPage] = useState(Number(initialIndex))
  let indexButtons: JSX.Element[]

  // Sync currentPage with initialIndex prop changes
  useEffect(() => {
    setCurrentPage(Number(initialIndex))
  }, [initialIndex])

  function goToPage(index: number) {
    setCurrentPage(index)
    onNewPage({ value: '' + index })
  }

  function onNextPage() {
    const nextPage = currentPage + 1
    if (nextPage > lastPage) {
      return
    }

    setCurrentPage(nextPage)
    onNewPage({ value: '' + nextPage })
  }

  function onPrevPage() {
    const prevPage = currentPage - 1
    if (prevPage < firstPage) {
      return
    }

    setCurrentPage(prevPage)
    onNewPage({ value: '' + prevPage })
  }

  indexButtons = buildPaginationButtonBar(lastPage, firstPage, currentPage, goToPage)
  const disabled1 = currentPage === firstPage
  const disabled2 = currentPage === lastPage
  return (
    <div className="flex gap-2">
      <button className={'Paginator--nav-btn ' + (disabled1 ? '' : 'white-btn-hover')} disabled={disabled1} onClick={onPrevPage}>
        Prev
      </button>
      {indexButtons}
      <button className={'Paginator--nav-btn ' + (disabled2 ? '' : 'white-btn-hover')} disabled={disabled2} onClick={onNextPage}>
        Next
      </button>
    </div>
  )
}


function DotSeparator(index: number): JSX.Element {
  return <span className="w-8 tracking-widest mt-1 text-center cursor-default" key={index}>...</span>
}

function IndexButton(index: number, currentPage: number, onGoToPage: (index: number) => void, uniqueKey: number): JSX.Element {
  return <PageIndexButton
    key={uniqueKey}
    pageIndex={index}
    goToPage={() => onGoToPage(index)}
    active={currentPage === index}
  />
}

// Fixed pagination logic - no overlapping conditions
function buildPaginationButtonBar(
  lastPage: number, firstPage: number, currentPage: number,
  onGoToPage: (index: number) => void
): JSX.Element[] {
  const indexButtons: JSX.Element[] = []

  // Simple case: show all pages if 9 or fewer
  if (lastPage <= 9) {
    for (let i = firstPage; i <= lastPage; i++) {
      indexButtons.push(IndexButton(i, currentPage, onGoToPage, i))
    }
    return indexButtons
  }

  // Complex case: smart pagination with ellipses
  const pages: (number | 'ellipsis')[] = []
  
  // Always show first page
  pages.push(firstPage)
  
  // Determine the range around current page
  let rangeStart = Math.max(firstPage + 1, currentPage - 2)
  let rangeEnd = Math.min(lastPage - 1, currentPage + 2)
  
  // Adjust range to show at least 5 middle pages when possible
  const rangeSize = rangeEnd - rangeStart + 1
  if (rangeSize < 5 && lastPage > 7) {
    if (rangeStart === firstPage + 1) {
      rangeEnd = Math.min(lastPage - 1, rangeStart + 4)
    } else if (rangeEnd === lastPage - 1) {
      rangeStart = Math.max(firstPage + 1, rangeEnd - 4)
    }
  }
  
  // Add ellipsis before range if needed
  if (rangeStart > firstPage + 1) {
    pages.push('ellipsis')
  }
  
  // Add the range around current page
  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i)
  }
  
  // Add ellipsis after range if needed
  if (rangeEnd < lastPage - 1) {
    pages.push('ellipsis')
  }
  
  // Always show last page (if different from first)
  if (lastPage > firstPage) {
    pages.push(lastPage)
  }
  
  // Convert to JSX elements with unique keys
  let keyCounter = 0
  pages.forEach(page => {
    if (page === 'ellipsis') {
      indexButtons.push(DotSeparator(keyCounter++))
    } else {
      indexButtons.push(IndexButton(page, currentPage, onGoToPage, keyCounter++))
    }
  })

  return indexButtons
}

export default Paginator
